/**
 * Title, pause, setup and controls — the identity layer.
 *
 * OWNER: piece:identity (title, menus & identity)
 *
 * WHAT GOVERNS THIS FILE
 * ---------------------------------------------------------------------------
 * `docs/IDENTITY.md`. Brett authored two assets and they outrank anything invented here:
 *
 *   assets/brand/grip-wordmark.svg   1494x498, 99 white paths on transparent
 *   assets/brand/grip-keyart.png     5748x2463
 *
 * Both are used as supplied. The mark is never redrawn, never re-lettered, never
 * re-coloured to the accent, never substituted with a system font. It is drawn through a
 * CSS alpha mask so the file's own geometry — including the tread knocked out of the
 * letters — is what reaches the screen, and the only thing this module decides is the one
 * flat value it is filled with (bone, matching the HUD's `#eef3f7`).
 *
 * THE PALETTE IS MEASURED, NOT DESCRIBED
 * ---------------------------------------------------------------------------
 * The prose in `docs/IDENTITY.md` is a guide to what to notice; where it and the image
 * disagree the image wins. So the ladder below was sampled out of the PNG itself (a
 * canvas readback in a headless page, two vertical transects at x=6% and x=72%):
 *
 *   f=0.00  #fddc9c   sky, pale cream-gold        <- top row, sd < 1.2 across the width
 *   f=0.62  #fdbd5c   sky at the horizon, amber
 *   f=0.66  #e5784f   first ridge band, orange
 *   f=0.73  #c15c52   terracotta
 *   f=0.76  #9a4b52   dusty rose
 *   f=0.81  #5e354c   plum / mauve
 *   f=0.86  #0d182c   foreground, and it holds this exact value to the last row
 *
 * One correction to the prose worth recording: the deepest band is NOT plum. It measures
 * R13 G24 B44 — a very dark, desaturated blue-black. The *travel* through the midtones is
 * toward plum and mauve exactly as described, but the ground the whole poster sits on is
 * cool. Every dark field in these screens uses that measured value, which is why the
 * title's letterbox fill and the key art's own foreground join invisibly.
 *
 * ROUND 2 — WHAT CHANGED AND WHY
 * ---------------------------------------------------------------------------
 * Round 1's title slid a flat menu shelf UP over the poster to make room, floored at
 * f=0.855 on the belief that the artwork was flat below there. A critic caught a
 * dead-straight horizontal edge through live artwork — conifers sheared mid-trunk, the
 * bright river-valley notch clipped — at 1280x720, 1440x810, 1600x900 and 2560x1080.
 *
 * The belief was then measured (`captures/w4-identity-probe/edges.mjs`): per column, the
 * row from which the column never changes again is p05 0.776 / p50 0.894 / p95 0.980.
 * **No horizontal line exists in this poster below which every column is flat**, so any
 * straight-edged plate over the image cuts something at some width. The layout no longer
 * looks for a safe fraction; the plate's top edge is now the artwork's bottom edge, and
 * room is bought by moving the poster inside an extended frame instead of covering it.
 * See `layout()`. `__gripMenus.geometry().plateOverArtPx` is the defect as a number.
 *
 * ROUND 3 — WHAT CHANGED AND WHY
 * ---------------------------------------------------------------------------
 * Round 2 passed the title and failed the three overlays. The critic's gap, measured:
 *
 *   > the opaque slab changes width on every navigation (781 / 1041 / 1165 px at
 *   > 1600x900) and is fixed-pixel, so it covers only 40.8% of a 2560x1080 screen with
 *   > the rest left as undesigned live world; inside it, content floats in unresolved
 *   > vertical void (~115 px under the rule, ~180 px above BACK, controls' right column
 *   > ending 400 px short). Pause -> setup -> controls reads as three separate screens
 *   > rather than one product, while the title reads as authored.
 *
 * Every clause of that was arithmetic, so every clause has a number now. The three
 * screens share ONE shell (`shell()`), one set of frame variables (`layoutOverlay()`),
 * and one masthead; only the body content differs. Measured over 18 shots — three
 * screens x six viewports, `captures/w4-identity-r3/PROBE.md`:
 *
 *              round 2                          round 3
 *   panel      781 / 1041 / 1165 px             one width per viewport, spread 0 px
 *   ultrawide  40.8% of the frame               60.0%, and no pixel ceiling anywhere
 *   void       ~115 px over, ~180 px under      symmetric, |over - under| = 0 px
 *   columns    controls' right ended 400 px up  0 px
 *   overflow   n/a                              0 px, with a measured compaction pass
 *   mark       type 25 px under a 400 px mark   clear space met on all four sides
 *
 * The frame is also a whole frame rather than a panel with leftover beside it: the band
 * ladder runs the full width at the top, the foot plate runs the full width at the
 * bottom, and the frozen game sits between them as a framed window that bleeds off the
 * right edge. `__gripMenus.compose()` reports all of it off the live DOM, and
 * `captures/w4-identity-probe/pxcheck.mjs` re-derives the same quantities from the
 * delivered PNGs with no DOM involved. The two agree to the pixel — which is how the
 * foot plate's 29 px overlap of the panel was found, since only the pixel side saw it.
 *
 * TWO REGISTERS, KEPT APART
 * ---------------------------------------------------------------------------
 * The identity is loud; the world is not. Nothing in this file touches the in-world
 * rendering, the camera, or any tuning constant. It is DOM over the canvas.
 *
 * WHY THIS IS INVISIBLE TO EVERY OTHER AGENT'S CAPTURES
 * ---------------------------------------------------------------------------
 * A title screen that shows at boot would put itself in front of nine other pieces'
 * screenshots, because the harness screenshots the page, not the canvas — the HUD is
 * already proof that DOM lands in captures. So:
 *
 *   - Under automation (`navigator.webdriver`) no screen is shown and no asset is
 *     fetched. The 4.4 MB key art is only requested the first time the title is actually
 *     displayed.
 *   - A second, independent guard catches a harness where `webdriver` is false: if the
 *     title is up and something *else* is stepping frames (the loop was stopped by this
 *     module, yet `update()` keeps arriving), the screens stand down for the session.
 *   - `?ui=title|pause|setup|controls` and `window.__gripMenus.show(id)` pin a screen so a
 *     critic can capture it deliberately. Pinning defeats both guards.
 *
 * PAUSE IS A REAL PAUSE
 * ---------------------------------------------------------------------------
 * It stops the game loop through `window.__grip.loop`, which is the harness hook the
 * capture tool already drives; `preserveDrawingBuffer` keeps the last frame on the canvas
 * behind the scrim. Nothing else in the app is touched, and the loop is only ever started
 * again if this module was the thing that stopped it.
 *
 * AUDIO
 * ---------------------------------------------------------------------------
 * `src/audio/audio.ts` builds its AudioContext on the first window gesture and this
 * module's buttons let that gesture through, so a click on RIDE unlocks the mix on its
 * own. It is also called explicitly (`__gripAudio.start()`) on the first interaction with
 * any screen, so the graph is up before the first frame of riding rather than on whatever
 * the player happens to press next. Measured on a page with no prior key event:
 * `__gripAudio.state()` goes `none -> running` on the first click of RIDE.
 *
 * HOW TO SEE IT / CHECK IT
 * ---------------------------------------------------------------------------
 * The shared harness cannot shoot these screens — `tools/capture/scenarios.mjs` belongs to
 * another piece and this one may not append to it — so the instruments live under
 * `captures/w4-identity-probe/`:
 *
 *   node captures/w4-identity-probe/shoot.mjs    <dir>   4 screens x 6 viewports + a
 *                                                       14-shape title sweep, real game
 *   node captures/w4-identity-probe/interact.mjs <dir>   14 behaviour assertions
 *   node captures/w4-identity-probe/pxcheck.mjs  <dir>   re-derives the frame geometry
 *                                                       from the PNGs, no DOM
 *   node captures/w4-identity-probe/rig.mjs      <dir>   fast look-only rig, no game
 *
 * By hand: `npm run dev`, then `/?ui=title`, `/?ui=pause`, `/?ui=setup`, `/?ui=controls`.
 */

import { registerSystem, type SystemContext } from '../core/systems';
import { Tuning } from '../core/tuning';

// ---------------------------------------------------------------------------
// Assets. `new URL(..., import.meta.url)` is the form Vite resolves statically, so these
// work from the dev server the capture harness boots and survive a production build.

const WORDMARK_URL = new URL('../../assets/brand/grip-wordmark.svg', import.meta.url).href;
/**
 * The web derivative, not the master.
 *
 * `grip-keyart.png` is 5748x2463 and 4.3 MB — it is the archival original and it stays
 * that way. Shipping it made the title screen's backdrop 93% of the entire download for
 * an image that is never drawn wider than the viewport. `grip-keyart-web.jpg` is the same
 * image at 2400 px and 310 KB (14x smaller) with no visible banding in the sky gradient
 * or softening of the wordmark's edges at display size.
 *
 * Regenerate it from the master if the art changes:
 *   sips -s format jpeg -s formatOptions 92 assets/brand/grip-keyart.png \
 *        --resampleWidth 2400 --out assets/brand/grip-keyart-web.jpg
 */
const KEYART_URL = new URL('../../assets/brand/grip-keyart-web.jpg', import.meta.url).href;

/** Aspect of the master, which the derivative preserves. The title's layout depends on it. */
const KEYART_ASPECT = 5748 / 2463;
/**
 * Where the wordmark baked into the key art sits, as a fraction of the image. Measured by
 * scanning the PNG for pure white — it is the only #ffffff element in the poster:
 * x 0.1392-0.8660, y 0.2436-0.7657 (`captures/w4-identity-probe/edges.mjs`).
 */
const KEYART_MARK_TOP_F = 0.2436;
const KEYART_MARK_BOTTOM_F = 0.7657;

/**
 * ROUND 2 — THE MEASUREMENT THAT KILLED THE OLD LAYOUT.
 *
 * Round 1 believed the poster's foreground "goes to its final dark value" at f=0.855 and
 * cut the menu shelf there. A critic found conifers sheared mid-trunk and the river-valley
 * accent clipped at 1280x720, 1440x810, 1600x900 and 2560x1080. So the claim was measured
 * properly: for every 8th column, the topmost row from which that column never again
 * changes by more than 4/255.
 *
 *   p05 0.776   p50 0.894   p95 0.980
 *
 * There is no horizontal line anywhere in this poster below which every column is flat.
 * A straight-edged plate over the artwork ALWAYS cuts something. The fix is therefore not
 * a better cut fraction — it is to stop cutting: the plate begins exactly where the
 * artwork ends and never one pixel above it. See `layout()`.
 */

/**
 * The poster's own first and last rows, so the frame can be extended past the image
 * instead of the image being trimmed to the frame. Both measured across the full width
 * (`captures/w4-identity-probe/ramp.mjs`):
 *
 *   row 0     rgb(253,220,156)  interior spread +-2, only the 1 px edge columns differ
 *   row H-1   rgb(13,24,44) = #0d182c   interior spread +-1
 *
 * and the sky's own vertical ramp between f=0.02 and f=0.12, per 1.0 of image height:
 *
 *   dR -3    dG -27    dB -95.5      (downward; the sky pales going up)
 *
 * which is enough to *continue* the sky above the artwork rather than butt a flat band
 * against a live gradient.
 */
const KEYART_SKY_TOP: readonly [number, number, number] = [253, 220, 156];
const KEYART_SKY_SLOPE: readonly [number, number, number] = [-3, -27, -95.5];
const KEYART_GROUND_HEX = '#0d182c';

/**
 * Where bone type is safe on the poster, measured as WCAG contrast of `#eef3f7` against
 * the worst pixel in the left 42% of each row (that is where the menu lives):
 *
 *   f 0.77-0.85   2.5-3.3     f 0.86-0.91   4.6-5.1     f 0.92-1.00   8.4-15.6
 *
 * So the menu block's top edge is placed at f=0.94 of the artwork when the frame allows
 * (>=8.4:1, and on flat ground rather than on a ridge), and is never allowed above 0.90.
 */
const TEXT_TARGET_F = 0.94;
const TEXT_FLOOR_F = 0.90;
/**
 * How much of the poster's *sky* may be cropped off the top to buy that. Sky is a smooth
 * vertical ramp with nothing in it; the mark's topmost spatter is at f=0.2436, so 0.14
 * still leaves 0.10 of the image — about a third of the mark's own height — clear above
 * it. Nothing below the sky is ever cropped.
 */
const MAX_TOP_CROP_F = 0.14;

// ---------------------------------------------------------------------------
// Palette. Sampled values above; `ACCENT` is the project's single accent, and the mark
// itself stays monochrome — the accent lives around it, never in it.

const C = {
  /** Re-measured in round 2 across the whole bottom row, not two transects. It holds. */
  ground: KEYART_GROUND_HEX,
  /**
   * Ground for the overlay panels.
   *
   * The measured foreground of the key art is `#0d182c`, which is cool, and a whole menu
   * built on it came back reading like a generic dark UI — `docs/IDENTITY.md` asks the
   * menus for *warm* grounds. This is the poster's own plum band (`#5e354c`) taken down
   * to a panel value rather than a colour invented from nothing, so the overlays are warm
   * and the title's shelf can still be the exact value the artwork ends on.
   */
  panel: '#1d1220',
  /**
   * The foot plate, one flat step below the panel. The key art's whole depth device is a
   * hard step in value between adjacent flat fields, and this is the cheapest honest use
   * of it in a menu: two grounds, one edge, no gradient. Round 3's first pass drew plate
   * and panel in the same value and the composition's bottom band only existed because
   * the tread happened to be sitting on it.
   */
  plate: '#150d18',
  plum: '#5e354c',
  rose: '#9a4b52',
  terracotta: '#c15c52',
  orange: '#e5784f',
  amber: '#fdbd5c',
  gold: '#fddc9c',
  /** The HUD's light value, so the two surfaces are one product. */
  bone: '#eef3f7',
  accent: '#c9683a',
} as const;

// ---------------------------------------------------------------------------
// The tread motif.
//
// docs/IDENTITY.md: "The tread is the motif. It is the one graphic device the rest of the
// UI can borrow from... Use it sparingly enough that it stays a signature." So it appears
// exactly once per screen, as the rule under the header, and nowhere else. It is drawn
// here as plain geometry rather than lifted out of the mark: borrowing the device is
// allowed, redrawing the artwork is not.

function treadTile(color: string): string {
  const c = encodeURIComponent(color);
  // Two staggered rows of chunky shoulder knobs on a 60-unit pitch, and nothing else.
  // Round 1 packed three dense rows (knobs, sipes, knobs) onto a 46-unit pitch at 19 px
  // tall and it read as a barcode; the first round-2 attempt kept the sipe row and read as
  // a chequer. At a 26 px rule a tread's signature is the SIZE and stagger of the knobs —
  // the sipes are below the size where they can be anything but noise, so they are gone.
  const svg =
    `<svg xmlns='http://www.w3.org/2000/svg' width='120' height='40' viewBox='0 0 120 40'>`
    + `<g fill='${c}'>`
    + `<rect x='0' y='0' width='46' height='17'/><rect x='60' y='0' width='46' height='17'/>`
    + `<rect x='120' y='0' width='46' height='17'/>`
    + `<rect x='-30' y='23' width='46' height='17'/><rect x='30' y='23' width='46' height='17'/>`
    + `<rect x='90' y='23' width='46' height='17'/>`
    + `</g></svg>`;
  return `url("data:image/svg+xml,${svg.replace(/"/g, "'").replace(/#/g, '%23')}")`;
}

/** The key art's band ladder as a hard-stepped bar. Flat fills, no blending.
 *  Round 1 ended the ladder on `C.panel`, which is the colour it is drawn on top of, so
 *  the last band was invisible and the rule looked truncated 12% short of the panel edge.
 *  It now ends on the poster's own foreground value, which is the band the artwork itself
 *  ends on. */
const LADDER = `linear-gradient(to right,`
  + `${C.gold} 0 12%,${C.amber} 12% 26%,${C.orange} 26% 42%,${C.terracotta} 42% 58%,`
  + `${C.rose} 58% 74%,${C.plum} 74% 88%,${C.ground} 88% 100%)`;

// ---------------------------------------------------------------------------
// Engine hooks. Every one is optional and every call is guarded: this module must never
// be the reason the game fails to boot, and it must never assume another piece's debug
// surface exists.

interface LoopHandle { start(): void; stop(): void }
interface WeatherHook {
  state: { kind: string; timeOfDay: number; autoCycle: boolean };
  force(kind: 'clear' | 'overcast' | 'rain'): void;
  time(hours: number): void;
}
interface PostHook { setTier(t: number): void; getTier(): number; reset(): void }
interface AudioHook { setEnabled(v: boolean): void; state(): string; start(): string }

interface HookWindow {
  __grip?: { loop?: LoopHandle };
  __gripWeather?: WeatherHook;
  __gripPost?: PostHook;
  __gripAudio?: AudioHook;
  __gripMenus?: unknown;
}
const hooks = (): HookWindow => window as unknown as HookWindow;

function attempt(fn: () => void): void {
  try { fn(); } catch { /* a settings row must never take the game down */ }
}

// ---------------------------------------------------------------------------
// Stylesheet. One injected block rather than inline styles on forty nodes, so the hover
// and selected states are real CSS and the whole look can be read in one place.
//
// House rules from docs/IDENTITY.md, and they are what most of these declarations are
// for: hard edges, flat fills, high contrast, no glassy panels, no soft drop shadows, no
// blur, no gradients under the mark.

const CSS = `
.gm-root{
  position:fixed;inset:0;z-index:60;display:none;
  color:${C.bone};
  font-family:'Arial Black','Helvetica Neue',Helvetica,Arial,system-ui,sans-serif;
  -webkit-font-smoothing:antialiased;
  user-select:none;
}
.gm-root[data-open='1']{display:block}
.gm-screen{position:absolute;inset:0;display:none;overflow:hidden}
.gm-screen[data-active='1']{display:block}

/* ---- the mark ------------------------------------------------------------
   Alpha mask over a flat fill: the supplied geometry, one value, nothing else. */
.gm-mark{
  display:block;
  aspect-ratio:1494/498;
  background-color:${C.bone};
  -webkit-mask-image:url(${WORDMARK_URL});mask-image:url(${WORDMARK_URL});
  -webkit-mask-repeat:no-repeat;mask-repeat:no-repeat;
  -webkit-mask-position:left center;mask-position:left center;
  -webkit-mask-size:contain;mask-size:contain;
}

/* ---- band ladder ---------------------------------------------------------
   Round 2 drew this only across the panel, so on every overlay it stopped dead at
   781 / 1041 / 1165 px and announced where the designed part ended. It now runs the
   full width of the frame on all three screens: it is the composition's top edge, not
   the panel's. */
.gm-ladder{position:absolute;left:0;right:0;top:0;height:var(--gm-cap,7px);background:${LADDER}}

/* ---- the wipe ------------------------------------------------------------
   docs/IDENTITY.md offers the tread and the band construction as UI devices, and names
   wipes specifically. This is the one moment of motion in the piece: leaving the title
   for the mountain. A flat panel crosses the frame with the poster's own band ladder on
   its leading edge, and the switch happens behind it. Nothing fades, nothing blurs —
   hard edges moving, which is the register.
   Never runs under automation (see standDown), so no capture can catch a half-wipe. */
@keyframes gm-wipe{
  0%{transform:translateX(-101%)}
  40%{transform:translateX(0)}
  56%{transform:translateX(0)}
  100%{transform:translateX(101%)}
}
.gm-wipe{
  position:fixed;inset:0;z-index:70;pointer-events:none;background:${C.panel};
  animation:gm-wipe 460ms cubic-bezier(.45,.02,.2,1) forwards;
}
/* Both edges carry the ladder: the right one leads while it covers, the left one leads
   while it reveals. The first version only had the right edge and the whole second half
   of the move was a plain rectangle sliding off. */
.gm-wipe::after,.gm-wipe::before{
  content:'';position:absolute;top:0;bottom:0;width:9px;
  background:linear-gradient(to bottom,
    ${C.gold} 0 14%,${C.amber} 14% 28%,${C.orange} 28% 44%,${C.terracotta} 44% 60%,
    ${C.rose} 60% 76%,${C.plum} 76% 100%);
}
.gm-wipe::after{right:-9px}
.gm-wipe::before{left:-9px}

/* ---- type ---------------------------------------------------------------- */
.gm-eyebrow{
  font-family:ui-monospace,SFMono-Regular,Menlo,monospace;
  font-size:12px;letter-spacing:.34em;text-transform:uppercase;
  color:${C.accent};
}
.gm-note{
  font-family:ui-monospace,SFMono-Regular,Menlo,monospace;
  font-size:12px;line-height:1.7;letter-spacing:.12em;text-transform:uppercase;
  color:rgba(238,243,247,.44);
}

/* ---- menu items ---------------------------------------------------------- */
.gm-menu{display:flex;flex-direction:column;align-items:flex-start;gap:2px}
/* Deliberately the root's wide black, NOT the condensed stack .gm-screenname uses, and
   this was decided by an A/B rather than by taste: captures/w4-identity-r3/ is the whole
   set rendered with the menu on the same Impact stack as the screen names. The mark is
   heavy AND condensed; Impact gives the condensation but at list sizes its stroke reads
   visibly lighter than the mark's, and the title menu came out weaker than the round-2
   version beside it. A condensed display face for the big screen names and a wide black
   for 30 px list items is the size-appropriate pairing, so that is what this keeps. */
.gm-item{
  appearance:none;background:none;border:0;padding:7px 0 7px 26px;margin:0;
  position:relative;cursor:pointer;color:rgba(238,243,247,.68);
  font:inherit;font-weight:900;text-transform:uppercase;
  font-size:clamp(21px,2.1vw,30px);line-height:1.06;letter-spacing:.045em;
  transition:color 80ms linear;
}
.gm-item::before{
  content:'';position:absolute;left:0;top:50%;transform:translateY(-50%);
  width:11px;height:0;background:${C.accent};transition:height 80ms linear;
}
.gm-item:hover,.gm-item:focus{color:${C.bone};outline:none}
.gm-item[data-sel='1']{color:${C.bone}}
.gm-item[data-sel='1']::before{height:calc(1em - 6px)}

/* ---- title ---------------------------------------------------------------
   THREE BANDS, and the poster is the middle one, whole.

     .gm-sky    0            -> artTop      continues the poster's sky ramp upward
     .gm-art    artTop       -> artBottom   the key art, full width, NEVER cropped
                                            horizontally and never covered
     .gm-plate  artBottom    -> bottom      the poster's own last row, extended down

   Both outer bands start from the artwork's exact edge value, so the two joins are steps
   of under 2/255 on the interior and there is no line to see. The artwork itself is not
   cut by anything: the plate's top edge IS the artwork's bottom edge. */
.gm-sky{position:absolute;left:0;right:0;top:0}
.gm-art{
  position:absolute;left:0;right:0;background-color:${C.ground};
  background-repeat:no-repeat;background-position:center top;background-size:100% 100%;
}
.gm-plate{
  position:absolute;left:0;right:0;bottom:0;background:${C.ground};
}
.gm-title-block{position:absolute;left:6.5%;right:6.5%;bottom:5%;font-size:var(--gm-tf,32px)}
.gm-title-block .gm-item{font-size:1em;padding:.2em 0 .2em .82em;line-height:1.06}
/* Ultrawide: the artwork alone is taller than the window, so the shelf under it is a
   strip. A row reads better in a strip than a shrunken stack. */
.gm-menu[data-row='1']{flex-direction:row;align-items:center;gap:1.1em}
.gm-title-block .gm-item::before{width:.36em}
.gm-title-foot{margin-top:.9em}

/* ---- overlay screens (pause / setup / controls) ---------------------------
   ROUND 3 — ONE COMPOSITION, THREE CONTENTS. This is the round-2 defect.

   Round 2 gave each screen its own slab: 781 / 1041 / 1165 px at 1600x900, each a
   clamp with a PIXEL CEILING, so on a 2560x1080 frame the widest of them still covered
   40.8% and the other 59% was live world nobody had designed. Inside the slab the
   content was pinned to the top of whatever height was left over, which put ~115 px of
   dead panel under setup's rule, ~180 px above BACK, and left controls' right column
   400 px short of its left. Walking pause -> setup -> controls moved every edge on the
   screen, so it read as three screens instead of one product.

   The frame is now fixed, identical on all three, and it is a WHOLE frame:

     .gm-ladder     the key art's band ladder, FULL WIDTH        top,    --gm-cap
     .gm-panel      solid ground, constant --gm-w on every screen, no px ceiling
     (the frozen game)  a framed window: ladder above, panel left, plate below,
                    bleeding off the right edge — an inset, not the leftover
     .gm-footplate  solid ground, FULL WIDTH, tread-capped       bottom, --gm-foot

   Every horizontal edge runs the whole width of the screen. That is the difference
   between a picture with something inset in it and a panel with a hole beside it.
   Nothing is a fixed pixel: --gm-w is a fraction of the frame with a lower floor only,
   so a wider screen gets a wider panel rather than more undesigned world. */

/* Near-neutral on purpose: a plum wash over the frozen frame would be pushing the
   identity's palette into the world, which docs/IDENTITY.md explicitly forbids. It
   darkens the game; it does not re-grade it. */
/* .66, not .76. Nothing is ever set over this — the panel and the plate carry every mark
   on these screens — so the only job the scrim has is to push the frozen frame back a
   plane. Crushing it to a flat dark field made the window read as a hole rather than as a
   picture the frame is holding, and on pause it made the game look like it had left. */
.gm-scrim{position:absolute;inset:0;background:rgba(9,9,13,.66)}
/* border-box, and this is not a detail. Round 2's panels were content-box with ~70 px of
   padding a side, so the width the stylesheet asked for and the width on the screen were
   140 px apart — the clamp said 640 and the slab measured 781. Any instrument reading the
   CSS would have disagreed with the screen. It is border-box so the number this file
   computes IS the number compose() measures off the DOM. */
.gm-panel{
  box-sizing:border-box;
  position:absolute;left:0;top:var(--gm-cap,7px);width:var(--gm-w,60%);bottom:var(--gm-foot,96px);
  background:${C.panel};
  display:grid;grid-template-rows:auto 1fr;
  padding:0 var(--gm-clear,56px);
  overflow:hidden;
}

/* ---- masthead ------------------------------------------------------------
   Identical on all three screens, which is most of what makes them one product: the
   supplied mark on the left, the screen's name on the right, one hairline under both.
   Round 2 headed pause with the mark and setup/controls with a display-type H1 and a
   tread rule, so the three heads were different heights and nothing lined up.
   CLEAR SPACE IS THE LAYOUT UNIT HERE. docs/IDENTITY.md asks for at least the cap
   height of the "G" clear on every side of the mark; measured off the supplied SVG the
   letters occupy y 48-410 of a 498-tall box, so cap height is 0.72 of the mark's height
   = 0.24 of its width. --gm-clear is exactly that, and it is what sets the padding
   above the mark, the gap to the rule under it, the gap to the name beside it, and the
   body's own inset. Round 2 put the "PAUSED" eyebrow 25 px under a 400 px mark, which
   is a quarter of what the mark is owed. */
.gm-head{
  display:flex;align-items:flex-end;justify-content:space-between;
  gap:var(--gm-clear,56px);
  padding:var(--gm-clear,56px) 0;
  border-bottom:1px solid rgba(238,243,247,.18);
}
.gm-head-mark{width:var(--gm-markw,232px);flex:0 0 auto}
/* The one place the type gets to echo the mark's proportions: the wordmark is heavy AND
   condensed, and a wide grotesque set next to it reads like a different company. No
   webfont ships (this piece owns one file and cannot add an asset), so this is the
   condensed end of what a browser already has, with a wide black as the fallback. */
.gm-screenname{
  font-family:Impact,Haettenschweiler,'Arial Narrow','Arial Black',sans-serif;
  font-weight:900;font-size:clamp(28px,4.1vh,52px);line-height:.84;
  letter-spacing:.02em;text-transform:uppercase;margin:0 0 .1em;
  color:${C.bone};white-space:nowrap;
}

/* ---- body ----------------------------------------------------------------
   align-content:center is the whole fix for the vertical void. The content is one
   block and whatever height is left over falls symmetrically above and below it, which
   is a margin. Round 2 pinned the block to the top and left the remainder in one lump
   at the bottom, which is a hole. Same rows, same numbers, different reading. */
.gm-body{
  min-height:0;display:grid;align-content:center;
  padding:var(--gm-clear,56px) 0;
  row-gap:clamp(24px,4.2vh,54px);
}
/* The overlay menus carry the same weight as the title's — this is the identity layer and
   its register is loud. A 30 px list on a 960 px panel read as a dialog. */
.gm-body>.gm-menu .gm-item{font-size:clamp(26px,2.7vw,42px);padding:.16em 0 .16em .62em}
.gm-body>.gm-menu .gm-item::before{width:.26em}
/* Two compaction steps, applied only when the body has measured itself as too tall for
   the frame (see fit()). A short window must give up spacing before it gives up content,
   and it must never scroll or clip: controls at 1024x768 overflowed by 130 px until this
   existed, and a screen that runs off the bottom of the panel is the same defect as one
   with a hole in it. */
.gm-body[data-dense='1']{padding:calc(var(--gm-clear,56px) * .45) 0;row-gap:clamp(16px,2.6vh,32px)}
.gm-body[data-dense='1'] .gm-keyrow{padding:clamp(4px,1vh,10px) 4px}
.gm-body[data-dense='1'] .gm-teach{font-size:12px;line-height:1.44}
.gm-body[data-dense='1']>.gm-menu .gm-item{font-size:clamp(22px,2.2vw,34px)}
.gm-body[data-dense='2']{padding:0;row-gap:14px}
.gm-body[data-dense='2'] .gm-keyrow{padding:3px}
.gm-body[data-dense='2'] .gm-teach{font-size:11.5px;line-height:1.38}
.gm-body[data-dense='2'] .gm-desc{min-height:0}

/* ---- foot plate ----------------------------------------------------------
   Full width, so the composition's bottom edge is the screen's bottom edge and the
   game window is closed off underneath rather than running out of frame.
   THE TREAD LIVES HERE, and this is the only place it appears on these screens. It is
   at the opposite end of the frame from the mark — round 2 set a tread rule 20 px under
   the mark's own knocked-out tread, which is not "sparingly", it is twice. */
.gm-footplate{
  /* border-box, for the same reason .gm-panel is, and this one was caught by the pixel
     cross-check rather than by the DOM probe: content-box made the plate --gm-tread px
     taller than declared, so it reached 29 px UP over the panel whose bottom is set from
     the same variable. Invisible (both grounds are dark) and still wrong — the body was
     centring itself in a box whose last 29 px were covered. */
  box-sizing:border-box;
  position:absolute;left:0;right:0;bottom:0;height:var(--gm-foot,96px);
  background:${C.plate};
  display:flex;align-items:center;justify-content:space-between;gap:32px;
  padding:var(--gm-tread,26px) var(--gm-clear,56px) 0;
}
/* The knobs have to be big enough to read AS tread. The first round-3 pass ran the tile
   at 19 px and it came back as a perforation — a dashed tear-line, not a tyre. Height is
   what fixes that (the tile is 3:1, so a taller band is also a longer knob), not density. */
.gm-footplate::before{
  content:'';position:absolute;left:0;right:0;top:0;height:var(--gm-tread,26px);
  background-repeat:repeat-x;background-position:left center;background-size:auto 100%;
  background-image:${treadTile('rgba(238,243,247,.26)')};
}
.gm-footplate .gm-item{padding:0 0 0 20px;font-size:clamp(20px,2vw,30px)}
.gm-footplate .gm-item::before{left:0;width:10px}

/* ---- setting rows --------------------------------------------------------- */
.gm-rows{border-top:1px solid rgba(238,243,247,.16)}
.gm-row{
  display:flex;align-items:center;justify-content:space-between;gap:24px;
  appearance:none;-webkit-appearance:none;
  border:0;border-bottom:1px solid rgba(238,243,247,.16);
  padding:clamp(13px,2.4vh,30px) 14px clamp(13px,2.4vh,30px) 16px;
  cursor:pointer;position:relative;background:none;
  width:100%;text-align:left;font:inherit;color:inherit;
}
.gm-row::before{
  content:'';position:absolute;left:0;top:0;bottom:0;width:4px;background:${C.accent};
  opacity:0;
}
.gm-row[data-sel='1']{background:rgba(201,104,58,.14)}
.gm-row[data-sel='1']::before{opacity:1}
.gm-row-label{
  font-family:ui-monospace,SFMono-Regular,Menlo,monospace;
  font-size:12px;letter-spacing:.2em;text-transform:uppercase;
  color:rgba(238,243,247,.6);
}
.gm-row[data-sel='1'] .gm-row-label{color:rgba(238,243,247,.82)}
.gm-row-val{
  font-weight:900;font-size:17px;letter-spacing:.06em;text-transform:uppercase;
  color:${C.bone};white-space:nowrap;
}
.gm-row-val span{color:rgba(238,243,247,.3);padding:0 12px;font-weight:700}
.gm-row-val b{font-weight:900;display:inline-block;min-width:5.6em;text-align:center}
.gm-row[data-sel='1'] .gm-row-val span{color:${C.accent}}

/* ---- the line under the rows that says what the selected one does ---------- */
.gm-desc{
  min-height:3.2em;max-width:58ch;
  font-family:ui-monospace,SFMono-Regular,Menlo,monospace;
  font-size:13px;line-height:1.6;color:rgba(238,243,247,.56);
  border-left:3px solid ${C.accent};padding-left:14px;
}

/* ---- controls grid --------------------------------------------------------
   TWO key columns and TWO teaching columns, both spanning the panel's full measure, so
   neither row of the body can end short of the other. Round 2 put eleven key rows in a
   left column and two paragraphs in a right one; the right column finished 400 px above
   the left and the screen had a visible corner missing. */
.gm-cols{display:grid;grid-template-columns:1fr 1fr;gap:clamp(16px,2vw,52px)}
.gm-root[data-narrow='1'] .gm-cols{grid-template-columns:1fr;gap:clamp(14px,2.4vh,28px)}
/* Ruled rows, the same rule at the same weight as setup's setting rows, so the two screens
   are recognisably the same page with different content in it. It also means each row
   spans its whole column: the first round-3 pass left the action text ending halfway
   across a 750 px column on an ultrawide, which is the same unresolved-space defect in
   the horizontal direction. */
.gm-keys{display:flex;flex-direction:column;border-top:1px solid rgba(238,243,247,.16)}
.gm-keyrow{
  display:flex;align-items:center;gap:14px;
  padding:clamp(7px,1.9vh,22px) 4px;
  border-bottom:1px solid rgba(238,243,247,.16);
}
.gm-key{display:flex;gap:6px;flex:0 0 auto;min-width:96px}
.gm-cap{
  border:2px solid rgba(238,243,247,.5);padding:3px 9px;min-width:20px;text-align:center;
  font-family:ui-monospace,SFMono-Regular,Menlo,monospace;
  font-size:12px;font-weight:700;letter-spacing:.06em;color:${C.bone};
}
.gm-act{
  font-family:ui-monospace,SFMono-Regular,Menlo,monospace;
  font-size:12px;letter-spacing:.14em;text-transform:uppercase;
  color:rgba(238,243,247,.62);
}
.gm-teach{
  font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-weight:400;
  max-width:40ch;font-size:clamp(13px,1.45vh,16px);line-height:1.52;
  color:rgba(238,243,247,.78);border-left:3px solid ${C.accent};padding-left:15px;
}

/* ---- stats block ----------------------------------------------------------
   Three across, on a rule, filling the panel's measure — a strip, not two islands
   floating over a floor. */
.gm-stats{
  display:grid;grid-template-columns:repeat(3,1fr);gap:clamp(16px,2vw,40px);
  border-top:1px solid rgba(238,243,247,.16);padding-top:clamp(16px,2.4vh,30px);
}
.gm-stat-k{
  font-family:ui-monospace,SFMono-Regular,Menlo,monospace;
  font-size:11px;letter-spacing:.24em;text-transform:uppercase;
  color:rgba(238,243,247,.42);
}
.gm-stat-v{font-weight:900;font-size:clamp(24px,3.2vh,38px);letter-spacing:-.01em;line-height:1.2;
  margin-top:6px}
.gm-stat-v i{font-style:normal;font-size:13px;font-weight:700;opacity:.5;padding-left:5px}
`;

// ---------------------------------------------------------------------------
// Small DOM helpers. Everything is built once at init and then only toggled.

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K, cls?: string, text?: string,
): HTMLElementTagNameMap[K] {
  const n = document.createElement(tag);
  if (cls) n.className = cls;
  if (text !== undefined) n.textContent = text;
  return n;
}

function mark(cls: string): HTMLDivElement {
  const d = el('div', `gm-mark ${cls}`);
  d.setAttribute('role', 'img');
  d.setAttribute('aria-label', 'GRIP');
  return d;
}

// ---------------------------------------------------------------------------
// Settings. Every row drives something real through another piece's own public debug
// surface; nothing here is decorative, and nothing is applied until the player asks for
// it, so a capture run that never touches a menu is bit-identical to one without this
// module.

interface Row {
  id: string;
  label: string;
  /** One line about what the setting does, shown under the rows for the selected one. */
  desc: string;
  options: readonly string[];
  index: number;
  apply(index: number): void;
  node?: HTMLButtonElement;
  valNode?: HTMLElement;
}

/** Hours passed to `__gripWeather.time()`, aligned with the option labels. */
const LIGHT_HOURS = [6.4, 8.0, 12.0, 15.5, 18.4, 20.2];

const ROWS: Row[] = [
  {
    id: 'weather',
    label: 'Conditions',
    desc: 'Rain darkens the dirt and takes grip with it.',
    options: ['Clear', 'Overcast', 'Rain'],
    index: 0,
    apply(i) {
      const w = hooks().__gripWeather;
      if (!w) return;
      attempt(() => w.force((['clear', 'overcast', 'rain'] as const)[i]));
    },
  },
  {
    id: 'light',
    label: 'Light',
    desc: 'Where the sun sits. Low light rakes across the ridges.',
    options: ['Dawn', 'Morning', 'Midday', 'Afternoon', 'Golden', 'Dusk'],
    index: 1,
    apply(i) {
      const w = hooks().__gripWeather;
      if (!w) return;
      attempt(() => w.time(LIGHT_HOURS[i] ?? 8));
    },
  },
  {
    id: 'cycle',
    label: 'Day cycle',
    desc: 'Let the clock run, or hold the hour you picked.',
    options: ['Held', 'Running'],
    index: 0,
    apply(i) {
      const w = hooks().__gripWeather;
      if (!w) return;
      attempt(() => { w.state.autoCycle = i === 1; });
    },
  },
  {
    id: 'detail',
    label: 'Detail',
    desc: 'Auto drops the post chain a tier when frames get long.',
    options: ['Auto', 'Low', 'Medium', 'High', 'Ultra'],
    index: 0,
    apply(i) {
      const p = hooks().__gripPost;
      if (!p) return;
      attempt(() => { if (i === 0) p.reset(); else p.setTier(i - 1); });
    },
  },
  {
    id: 'sound',
    label: 'Sound',
    desc: 'Tyre on dirt, suspension, wind, and the mountain around you.',
    options: ['On', 'Off'],
    index: 0,
    apply(i) {
      const a = hooks().__gripAudio;
      if (!a) return;
      attempt(() => { a.setEnabled(i === 0); if (i === 0) a.start(); });
    },
  },
  {
    id: 'telemetry',
    label: 'Telemetry',
    desc: 'The debug readout. Backtick toggles it at any time.',
    options: ['Hidden', 'Shown'],
    index: 0,
    // The HUD owns the debug panel and binds it to the backtick key. Driving that binding
    // is the whole interface — no reaching into another piece's DOM or its file.
    apply() {
      attempt(() => window.dispatchEvent(
        new KeyboardEvent('keydown', { code: 'Backquote', bubbles: true }),
      ));
    },
  },
];

/** Mirrors `KEY_MAP` in src/input/controls.ts. Kept in step by hand; it is the reference
 *  a new player reads, and controls.ts does not export the map. */
const KEYS: ReadonlyArray<readonly [string[], string]> = [
  [['A', 'D'], 'Lean — push right, go right'],
  [['W', 'S'], 'Weight forward / back'],
  [['J'], 'Front brake'],
  [['K'], 'Rear brake'],
  [['SHIFT'], 'Pedal'],
  [['SPACE'], 'Pump · hop · preload'],
  [['F'], 'Manual'],
  [['Q', 'E'], 'Whip / spin in the air'],
  [['R'], 'Reset to the trailhead'],
  [['ESC'], 'Pause'],
  [['`'], 'Telemetry'],
];

// ---------------------------------------------------------------------------

type ScreenId = 'title' | 'pause' | 'setup' | 'controls';

/** What the title layout resolved to, in CSS pixels. Published through `__gripMenus`. */
interface TitleGeom {
  vw: number; vh: number;
  artTop: number; artBottom: number; artH: number;
  plateTop: number; plateOverArtPx: number;
  blockTop: number; markTop: number; markBottom: number; clearSpacePx: number;
  textAtArtF: number; topCropF: number; row: boolean;
}

/**
 * What the overlay frame resolved to, in CSS pixels. Round 2's defect was measured in
 * exactly these terms — three different panel widths, a pixel ceiling that left 59% of an
 * ultrawide undesigned — so the fix is published as the same numbers and a probe can
 * assert on them instead of on a description.
 */
interface OverlayGeom {
  vw: number; vh: number;
  /** Identical on pause, setup and controls, by construction: one set of vars, one shell. */
  panelW: number; panelPct: number;
  capH: number; footH: number; padX: number;
  /** Mark width, and the clear space it is owed (cap height of the "G"). */
  markW: number; clearPx: number;
  narrow: boolean;
}

/**
 * The poster's sky, continued upward. `bandF` is the height of the band above the
 * artwork as a fraction of the artwork's own height; the top stop is the measured ramp
 * extrapolated by that much, and the bottom stop is the poster's exact first row, so the
 * join is a step of under 2/255. Extrapolation is capped at 1.2 image-heights, past
 * which the ramp would leave the gamut anyway.
 */
function skyExtension(bandF: number): string {
  const k = Math.max(0, Math.min(1.2, bandF));
  const clamp = (v: number) => Math.max(0, Math.min(255, Math.round(v)));
  const top = KEYART_SKY_TOP.map((v, i) => clamp(v - KEYART_SKY_SLOPE[i] * k));
  const bot = KEYART_SKY_TOP.map((v) => clamp(v));
  return `linear-gradient(to bottom,rgb(${top.join(',')}) 0%,rgb(${bot.join(',')}) 100%)`;
}

interface Screen {
  root: HTMLDivElement;
  items: HTMLButtonElement[];
  sel: number;
}

class Menus {
  private readonly root = el('div', 'gm-root');
  private readonly screens = new Map<ScreenId, Screen>();
  private current: ScreenId | null = null;
  /** Where BACK goes from setup/controls. */
  private returnTo: ScreenId = 'title';
  /** Only ever restart a loop this module stopped itself. */
  private stoppedLoop = false;
  /** Set once a screen has been displayed and the key art requested. */
  private artLoaded = false;
  /** Pinned by ?ui= or by __gripMenus.show(); defeats both automation guards. */
  private pinned = false;
  /** True once the module has decided it is running under a capture harness. */
  private standDown = false;
  private gestureSeen = false;
  private plate: HTMLDivElement | null = null;
  private artEl: HTMLDivElement | null = null;
  private skyEl: HTMLDivElement | null = null;
  private descNode: HTMLDivElement | null = null;
  private titleBlock: HTMLDivElement | null = null;
  private titleMenu: HTMLDivElement | null = null;
  /** Gap between the lowest pixel of the mark and the top of the menu block, px. */
  private clearSpacePx = 0;
  /** Last title layout, published for the probe so the seam invariant is a number. */
  private geom: TitleGeom | null = null;
  /** Last overlay frame, likewise. Identical for all three screens by construction. */
  private oGeom: OverlayGeom | null = null;

  // Run readout, tracked here so the pause screen can say something true about the ride.
  private distanceM = 0;
  private topSpeedKmh = 0;
  private rideSec = 0;
  private lastX = 0;
  private lastZ = 0;
  private haveLast = false;
  private statDist: HTMLDivElement | null = null;
  private statSpeed: HTMLDivElement | null = null;
  private statTime: HTMLDivElement | null = null;

  build(parent: HTMLElement): void {
    const style = el('style');
    style.textContent = CSS;
    document.head.appendChild(style);

    this.buildTitle();
    this.buildPause();
    this.buildSetup();
    this.buildControls();
    parent.appendChild(this.root);

    window.addEventListener('keydown', (e) => this.onKey(e));
    window.addEventListener('resize', () => this.layout());
    this.root.addEventListener('pointerdown', () => this.firstGesture(), { passive: true });
    this.layout();
  }

  // -- screens --------------------------------------------------------------

  private screen(id: ScreenId): HTMLDivElement {
    const s = el('div', 'gm-screen');
    s.dataset.id = id;
    this.root.appendChild(s);
    this.screens.set(id, { root: s, items: [], sel: 0 });
    return s;
  }

  private item(id: ScreenId, label: string, onPick: () => void): HTMLButtonElement {
    const b = el('button', 'gm-item', label);
    b.type = 'button';
    const s = this.screens.get(id)!;
    const idx = s.items.length;
    s.items.push(b);
    b.addEventListener('pointerenter', () => this.select(id, idx));
    b.addEventListener('click', () => { this.firstGesture(); this.select(id, idx); onPick(); });
    return b;
  }

  private buildTitle(): void {
    const s = this.screen('title');
    const sky = el('div', 'gm-sky');
    this.skyEl = sky;
    const art = el('div', 'gm-art');
    this.artEl = art;
    const plate = el('div', 'gm-plate');
    this.plate = plate;
    s.appendChild(sky);
    s.appendChild(art);
    s.appendChild(plate);

    const block = el('div', 'gm-title-block');
    this.titleBlock = block;

    const menu = el('div', 'gm-menu');
    this.titleMenu = menu;
    menu.appendChild(this.item('title', 'Ride', () => this.startRiding()));
    menu.appendChild(this.item('title', 'Setup', () => this.open('setup', 'title')));
    menu.appendChild(this.item('title', 'Controls', () => this.open('controls', 'title')));
    block.appendChild(menu);

    const foot = el('div', 'gm-note gm-title-foot');
    foot.textContent = `Alpha build · seed ${Tuning.world.seed} · esc pauses`;
    block.appendChild(foot);

    s.appendChild(block);
  }

  /**
   * The shared overlay shell, and it is shared by all three screens down to the pixel:
   * band-ladder cap across the full frame, dimmed game behind, solid panel of constant
   * width on the left, solid foot plate across the full frame. `body` is the only thing
   * a screen fills in, and `footLeft` is the only thing that differs in the plate.
   *
   * Returns the body element. Everything about where that body sits — its top, its
   * bottom, its measure, the space above and below its content — is identical on pause,
   * setup and controls, at every viewport. That is the round-2 defect closed.
   */
  private shell(id: ScreenId, name: string, hint: string): HTMLDivElement {
    const s = this.screens.get(id)!.root;
    s.appendChild(el('div', 'gm-scrim'));

    const p = el('div', 'gm-panel');
    const head = el('div', 'gm-head');
    head.appendChild(mark('gm-head-mark'));
    head.appendChild(el('h1', 'gm-screenname', name));
    p.appendChild(head);
    const body = el('div', 'gm-body');
    p.appendChild(body);
    s.appendChild(p);

    // The plate is built here but its button is registered by finishShell(), AFTER the
    // screen's own menu items, so arrow order follows reading order down the screen
    // instead of jumping from the bottom-left button back up to the top of the list.
    const plate = el('div', 'gm-footplate');
    plate.appendChild(el('div', 'gm-menu'));
    plate.appendChild(el('div', 'gm-note', hint));
    s.appendChild(plate);

    s.appendChild(el('div', 'gm-ladder'));
    return body;
  }

  /**
   * The plate's left slot, added last. It is always the same control on all three
   * screens — the one that leaves this screen — which is why RESUME is not also in the
   * pause list: one exit affordance, in one place, in the same place every time.
   */
  private finishShell(id: ScreenId): void {
    const slot = this.screens.get(id)!.root.querySelector<HTMLElement>('.gm-footplate .gm-menu');
    if (!slot) return;
    slot.appendChild(this.item(
      id,
      id === 'pause' ? 'Resume' : 'Back',
      () => (id === 'pause' ? this.startRiding() : this.open(this.returnTo)),
    ));
  }

  private buildPause(): void {
    this.screen('pause');
    const body = this.shell('pause', 'Paused', 'Esc resumes');

    const menu = el('div', 'gm-menu');
    menu.appendChild(this.item('pause', 'Restart run', () => this.restart()));
    menu.appendChild(this.item('pause', 'Setup', () => this.open('setup', 'pause')));
    menu.appendChild(this.item('pause', 'Controls', () => this.open('controls', 'pause')));
    menu.appendChild(this.item('pause', 'Leave to title', () => this.open('title')));
    body.appendChild(menu);

    // Three across on a rule, spanning the panel's measure. Two floating stats pinned to
    // the panel floor was what left a 250 px hole under them in round 2.
    const stats = el('div', 'gm-stats');
    const cell = (k: string): HTMLDivElement => {
      const d = el('div');
      d.appendChild(el('div', 'gm-stat-k', k));
      const v = el('div', 'gm-stat-v');
      d.appendChild(v);
      stats.appendChild(d);
      return v;
    };
    this.statDist = cell('Ridden');
    this.statSpeed = cell('Top speed');
    this.statTime = cell('Run time');
    body.appendChild(stats);
    this.finishShell('pause');
  }

  private buildSetup(): void {
    this.screen('setup');
    const body = this.shell('setup', 'Setup', 'Arrows change · esc goes back');

    const rows = el('div', 'gm-rows');
    for (const r of ROWS) {
      const b = el('button', 'gm-row');
      b.type = 'button';
      b.appendChild(el('div', 'gm-row-label', r.label));
      const v = el('div', 'gm-row-val');
      const valText = el('b');
      valText.textContent = r.options[r.index];
      v.appendChild(el('span', undefined, '‹'));
      v.appendChild(valText);
      v.appendChild(el('span', undefined, '›'));
      b.appendChild(v);
      r.node = b;
      r.valNode = valText;

      const idx = ROWS.indexOf(r);
      b.addEventListener('pointerenter', () => this.selectRow(idx));
      b.addEventListener('click', (e) => {
        this.firstGesture();
        this.selectRow(idx);
        const rect = b.getBoundingClientRect();
        // Clicking the left third steps back, anywhere else steps forward. Keeps the
        // whole row a target without inventing two more buttons per line.
        this.cycle(r, e.clientX < rect.left + rect.width * 0.25 ? -1 : 1);
      });
      rows.appendChild(b);
    }
    body.appendChild(rows);
    this.descNode = el('div', 'gm-desc');
    body.appendChild(this.descNode);
    this.finishShell('setup');
  }

  private buildControls(): void {
    this.screen('controls');
    const body = this.shell('controls', 'Controls', 'A gamepad works too');

    // Two key columns and two teaching columns, both spanning the panel's whole measure.
    // Round 2 stacked eleven key rows in a left column beside two paragraphs in a right
    // one, so the right column stopped 400 px above the left and the screen had a hole
    // in its bottom-right corner at every viewport.
    const keys = el('div', 'gm-cols');
    const half = Math.ceil(KEYS.length / 2);
    for (const part of [KEYS.slice(0, half), KEYS.slice(half)]) {
      const grid = el('div', 'gm-keys');
      for (const [caps, action] of part) {
        const row = el('div', 'gm-keyrow');
        const k = el('div', 'gm-key');
        for (const c of caps) k.appendChild(el('div', 'gm-cap', c));
        row.appendChild(k);
        row.appendChild(el('div', 'gm-act', action));
        grid.appendChild(row);
      }
      keys.appendChild(grid);
    }
    body.appendChild(keys);

    const teachRow = el('div', 'gm-cols');
    const teach = el('div', 'gm-teach');
    teach.textContent =
      'Hold space into a compression and let go at the lip. Held is you driving the bike '
      + 'into the ground, released is the pop — pump, hop and preload are one movement, '
      + 'and the terrain decides which one it turns out to be.';
    teachRow.appendChild(teach);
    const teach2 = el('div', 'gm-teach');
    teach2.textContent =
      'Lean is what turns the bike. Push toward the corner and hold it; the bars '
      + 'countersteer for you. Weight back on the steeps — the front wheel only steers '
      + 'while it still has load on it.';
    teachRow.appendChild(teach2);
    body.appendChild(teachRow);
    this.finishShell('controls');
  }

  // -- state ----------------------------------------------------------------

  open(id: ScreenId, from?: ScreenId): void {
    if (from) this.returnTo = from;
    if (id === 'title') this.returnTo = 'title';
    if (id === 'title' && !this.artLoaded) {
      this.artLoaded = true;
      if (this.artEl) this.artEl.style.backgroundImage = `url(${KEYART_URL})`;
    }
    for (const [key, s] of this.screens) s.root.dataset.active = key === id ? '1' : '0';
    this.root.dataset.open = '1';
    this.current = id;
    this.pauseLoop();
    this.layout();
    if (id === 'pause') this.refreshStats();
    // Pause opens on RESUME — the least destructive thing Enter can do, and the last item
    // in reading order. Setup and controls open on their own first item.
    this.select(id, id === 'pause' ? this.screens.get('pause')!.items.length - 1 : 0);
    // Setup opens with the rows live rather than with BACK highlighted, so the first
    // arrow press changes a setting instead of hunting for one.
    if (id === 'setup') this.selectRow(this.rowSel);
  }

  close(): void {
    this.root.dataset.open = '0';
    this.current = null;
    for (const s of this.screens.values()) s.root.dataset.active = '0';
  }

  private startRiding(): void {
    const fromTitle = this.current === 'title';
    this.firstGesture();
    this.close();
    this.resumeLoop();
    // Only leaving the title, not every unpause: a wipe on each ESC would wear out fast.
    if (fromTitle) this.wipe();
  }

  /** One-shot transition element. Self-removing; no state, nothing to leak. */
  private wipe(): void {
    if (this.standDown && !this.pinned) return;
    const w = el('div', 'gm-wipe');
    document.body.appendChild(w);
    window.setTimeout(() => w.remove(), 700);
  }

  private restart(): void {
    this.distanceM = 0;
    this.topSpeedKmh = 0;
    this.rideSec = 0;
    this.haveLast = false;
    this.startRiding();
    // The game already has a reset path — R, through the InputManager the sim reads. Use
    // it rather than reaching into the physics object from a system.
    attempt(() => {
      window.dispatchEvent(new KeyboardEvent('keydown', { code: 'KeyR', bubbles: true }));
      window.setTimeout(() => {
        window.dispatchEvent(new KeyboardEvent('keyup', { code: 'KeyR', bubbles: true }));
      }, 120);
    });
  }

  private pauseLoop(): void {
    if (this.stoppedLoop) return;
    const loop = hooks().__grip?.loop;
    if (!loop) return;
    attempt(() => { loop.stop(); this.stoppedLoop = true; });
  }

  private resumeLoop(): void {
    if (!this.stoppedLoop) return;
    const loop = hooks().__grip?.loop;
    this.stoppedLoop = false;
    if (!loop) return;
    attempt(() => loop.start());
  }

  private firstGesture(): void {
    if (this.gestureSeen) return;
    this.gestureSeen = true;
    // Browsers block audio before a gesture, and until now nothing in the product had a
    // gesture to offer. A click on a menu is the natural place to unlock it.
    attempt(() => { hooks().__gripAudio?.start(); });
  }

  // -- selection ------------------------------------------------------------

  private select(id: ScreenId, index: number): void {
    const s = this.screens.get(id);
    if (!s || !s.items.length) return;
    s.sel = (index + s.items.length) % s.items.length;
    for (let i = 0; i < s.items.length; i++) {
      s.items[i].dataset.sel = i === s.sel ? '1' : '0';
    }
  }

  private rowSel = 0;

  private selectRow(index: number): void {
    this.rowSel = (index + ROWS.length) % ROWS.length;
    for (let i = 0; i < ROWS.length; i++) {
      if (ROWS[i].node) ROWS[i].node!.dataset.sel = i === this.rowSel ? '1' : '0';
    }
    if (this.descNode) this.descNode.textContent = ROWS[this.rowSel].desc;
    // Moving into the rows takes the highlight off BACK, so the screen has one focus.
    const s = this.screens.get('setup');
    if (s) for (const it of s.items) it.dataset.sel = '0';
  }

  private cycle(r: Row, dir: number): void {
    r.index = (r.index + dir + r.options.length) % r.options.length;
    if (r.valNode) r.valNode.textContent = r.options[r.index];
    r.apply(r.index);
  }

  private onKey(e: KeyboardEvent): void {
    if (e.code === 'Escape') {
      e.preventDefault();
      // Deliberately NOT suppressed under automation. The harness drives the sim through
      // `input.scripted`, never through real key events, so nothing it does can reach
      // here — and if a future scenario ever did, the guard in tick() closes the screen
      // on the next stepped frame. Blocking it instead only made the piece untestable.
      if (this.current === null) this.open('pause');
      else if (this.current === 'setup' || this.current === 'controls') this.open(this.returnTo);
      else if (this.current === 'pause') this.startRiding();
      return;
    }
    if (this.current === null) return;
    const s = this.screens.get(this.current)!;
    const onRows = this.current === 'setup' && s.items.every((i) => i.dataset.sel !== '1');

    switch (e.code) {
      case 'ArrowDown':
      case 'Tab':
        e.preventDefault();
        if (this.current === 'setup') {
          if (onRows && this.rowSel === ROWS.length - 1) this.select('setup', 0);
          else if (onRows) this.selectRow(this.rowSel + 1);
          else this.selectRow(0);
        } else this.select(this.current, s.sel + 1);
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (this.current === 'setup') {
          if (onRows && this.rowSel === 0) this.select('setup', 0);
          else if (onRows) this.selectRow(this.rowSel - 1);
          else this.selectRow(ROWS.length - 1);
        } else this.select(this.current, s.sel - 1);
        break;
      case 'ArrowLeft':
        if (onRows) { e.preventDefault(); this.cycle(ROWS[this.rowSel], -1); }
        break;
      case 'ArrowRight':
        if (onRows) { e.preventDefault(); this.cycle(ROWS[this.rowSel], 1); }
        break;
      case 'Enter':
      case 'NumpadEnter':
        e.preventDefault();
        this.firstGesture();
        if (onRows) this.cycle(ROWS[this.rowSel], 1);
        else s.items[s.sel]?.click();
        break;
      default:
        break;
    }
  }

  // -- layout ---------------------------------------------------------------

  /**
   * The overlay frame. ONE set of numbers, written to the root, used by all three
   * screens — so pause, setup and controls cannot disagree about where anything is.
   *
   * ROUND 2'S DEFECT, AS ARITHMETIC. Each screen carried its own
   * `clamp(<floor>px, <pct>, <ceiling>px)`. At 1600x900 that resolved to 781 / 1041 /
   * 1165 px, so every navigation moved the panel edge; at 2560x1080 the *pixel ceiling*
   * bound instead of the percentage and the widest screen still covered 40.8%, leaving
   * 59% of the frame as live world with nothing designed on it.
   *
   * The replacement has no upper pixel bound at all. Width is a constant fraction of the
   * frame with a floor only, so a wider screen buys a wider panel, never more leftover:
   *
   *     panelW = min(vw, max(0.60 * vw, 660))
   *
   *   1600x900   -> 960  (60.0%)      2560x1080 -> 1536 (60.0%)
   *   1920x1080  -> 1152 (60.0%)      1280x1024 ->  768 (60.0%)
   *   1024x768   ->  660 (64.5%)       800x600  ->  660 (82.5%)
   *
   * The floor is the only piece of pixel arithmetic and it only ever *widens* the panel
   * on a small window, which is the opposite of the failure it replaces.
   *
   * `--gm-clear` is the mark's clear space and it is the unit the whole panel is built
   * from. docs/IDENTITY.md asks for at least the cap height of the "G" on every side;
   * measured off the supplied SVG the letterforms run y 48-410 in a 498-tall box, so cap
   * height is 0.727 of the mark's height, and the mark is 3:1, so
   *
   *     clear = 0.727 * markW / 3 = 0.242 * markW
   *
   * That number is the padding over the mark, the gap to the rule under it, the gap to
   * the screen name beside it, and the body's own inset. Round 2 set the mark 400 px wide
   * and put type 25 px under it — 26% of what the mark is owed.
   */
  private layoutOverlay(): OverlayGeom {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const cl = (lo: number, v: number, hi: number) => Math.round(Math.max(lo, Math.min(hi, v)));
    const panelW = Math.round(Math.min(vw, Math.max(vw * 0.6, 660)));
    // Never below the 180 px minimum docs/IDENTITY.md sets for the full mark.
    const markW = cl(190, vw * 0.17, 320);
    const clearPx = Math.round(markW * 0.242);
    const capH = 7;
    const footH = cl(78, vh * 0.115, 130);
    // The panel's own margin IS the mark's clear space — one number, so nothing can quietly
    // crowd the spatter. The first round-3 pass had a separate 42 px horizontal padding and
    // that, not the head's padding, turned out to be the binding constraint: the mark was
    // owed 56 px and got 42 on its left side at every viewport shot.
    const padX = clearPx;
    // Two key columns are still the better answer well past the point where the action
    // text starts wrapping — one column of eleven rows is what overflowed the 1024x768
    // body by 130 px. Only collapse when a column can no longer hold the caps plus a word.
    const narrow = panelW - padX * 2 < 460;

    const st = this.root.style;
    st.setProperty('--gm-w', `${panelW}px`);
    st.setProperty('--gm-cap', `${capH}px`);
    st.setProperty('--gm-foot', `${footH}px`);
    st.setProperty('--gm-markw', `${markW}px`);
    st.setProperty('--gm-clear', `${clearPx}px`);
    st.setProperty('--gm-tread', `${cl(22, footH * 0.27, 34)}px`);
    this.root.dataset.narrow = narrow ? '1' : '0';

    this.fit();
    return {
      vw, vh, panelW, panelPct: +((panelW / vw) * 100).toFixed(1),
      capH, footH, padX, markW, clearPx, narrow,
    };
  }

  /**
   * Measure the visible overlay's body against the height it actually has and step the
   * compaction down until it fits. Two steps, then stop — if content still does not fit
   * after step 2 the window is smaller than anything this product targets, and clipping
   * one line is better than a scrollbar in a game menu.
   *
   * Measured, not assumed: `need` is the sum of the body's children plus its row gaps,
   * `have` is its content box. Only the screen that is on screen has boxes to measure
   * (the others are `display:none`), which is exactly the one that matters.
   */
  private fit(): void {
    for (const s of this.screens.values()) {
      const body = s.root.querySelector<HTMLElement>('.gm-body');
      if (!body || body.offsetParent === null) continue;
      for (let step = 0; step <= 2; step++) {
        body.dataset.dense = String(step);
        const cs = getComputedStyle(body);
        const gap = parseFloat(cs.rowGap) || 0;
        const kids = Array.from(body.children) as HTMLElement[];
        const need = kids.reduce((a, k) => a + k.offsetHeight, 0)
          + gap * Math.max(0, kids.length - 1);
        const have = body.clientHeight
          - (parseFloat(cs.paddingTop) || 0) - (parseFloat(cs.paddingBottom) || 0);
        if (need <= have) break;
      }
    }
  }

  /**
   * The title is the key art, whole, in a frame that is extended to fit rather than an
   * image that is trimmed to fit.
   *
   * ROUND 1 CUT THE ARTWORK AND THAT IS THE DEFECT THIS ROUND EXISTS TO CLOSE. It drew
   * the poster from the top of the frame at full width and then slid a flat plate UP over
   * it to make room for the menu, floored at f=0.855 because the foreground was believed
   * to be flat below there. It is not — measured, no such line exists at any fraction
   * (see the block comment on TEXT_TARGET_F). The result was a dead-straight horizontal
   * edge through live artwork at four of the commonest desktop sizes: conifers sheared
   * mid-trunk, the bright river-valley notch clipped.
   *
   * The rule now is absolute and there is no fraction left to get wrong:
   *
   *     the plate's top edge IS the artwork's bottom edge. Never above it. Ever.
   *
   * Room for the menu is bought by moving the *poster*, not by covering it:
   *
   *   1. The artwork is always exactly viewport-wide, so its width is never cropped and
   *      the clear space Brett left around the spatter is never eaten into.
   *   2. Its bottom edge is placed so the menu's top line lands at f=0.94 of the image —
   *      on flat foreground, at >=8.4:1 against bone — and never above f=0.90.
   *   3. What that leaves above the artwork is filled with the poster's own sky, extended
   *      upward along its measured ramp. What it leaves below is filled with the poster's
   *      own last row. Both joins are steps of <2/255.
   *   4. If the frame is short enough that step 2 would push the artwork's top off the
   *      screen, up to MAX_TOP_CROP_F of the *sky* — and only the sky — is allowed to go.
   *      Past that the menu becomes a row, which is shorter, before anything else gives.
   */
  private layout(): void {
    this.oGeom = this.layoutOverlay();
    const block = this.titleBlock;
    const menu = this.titleMenu;
    if (!this.plate || !block || !menu || !this.artEl || !this.skyEl) return;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const artH = vw / KEYART_ASPECT;
    const base = Math.max(19, Math.min(34, vw * 0.0215));
    const margin = Math.max(24, vh * 0.05);

    // Two candidate menus — a stack and a row. Measure both, then keep the poster.
    interface Plan {
      row: boolean; font: number; blockTop: number;
      artTop: number; artBottom: number; topCropF: number; textF: number;
    }
    const plan = (row: boolean): Plan => {
      menu.dataset.row = row ? '1' : '0';
      let font = base;
      block.style.setProperty('--gm-tf', `${font.toFixed(1)}px`);
      let blockH = block.offsetHeight;
      // A very short window must not let the menu eat the poster; shrink type first.
      const cap = vh * 0.40;
      if (blockH > cap) {
        font = Math.max(13, base * (cap / blockH));
        block.style.setProperty('--gm-tf', `${font.toFixed(1)}px`);
        blockH = block.offsetHeight;
      }
      const blockTop = vh - blockH - margin;
      // Where the artwork's bottom edge wants to be, clamped so it can neither run past
      // the bottom of the frame nor crop more sky than MAX_TOP_CROP_F allows.
      const artBottom = Math.max(
        (1 - MAX_TOP_CROP_F) * artH,
        Math.min(vh, blockTop + (1 - TEXT_TARGET_F) * artH),
      );
      const artTop = artBottom - artH;
      return {
        row, font, blockTop, artTop, artBottom,
        topCropF: artTop < 0 ? -artTop / artH : 0,
        textF: (blockTop - artTop) / artH,
      };
    };
    const ok = (p: Plan) => p.topCropF <= MAX_TOP_CROP_F + 1e-6 && p.textF >= TEXT_FLOOR_F;
    const stack = plan(false);
    let chosen = stack;
    if (!ok(stack)) {
      const row = plan(true);
      chosen = ok(row) || row.textF > stack.textF ? row : stack;
    }
    // plan() left the DOM on whichever candidate it measured last; put the winner back.
    menu.dataset.row = chosen.row ? '1' : '0';
    block.style.setProperty('--gm-tf', `${chosen.font.toFixed(1)}px`);
    block.style.bottom = `${Math.round(margin)}px`;

    // Integer edges, and the plate's top is literally the art element's bottom, so no
    // rounding can ever open a seam or let the plate creep over the image.
    const artTop = Math.round(chosen.artTop);
    const artBottom = Math.round(chosen.artBottom);
    this.artEl.style.top = `${artTop}px`;
    this.artEl.style.height = `${Math.max(1, artBottom - artTop)}px`;
    this.plate.style.top = `${artBottom}px`;
    this.skyEl.style.height = `${Math.max(0, artTop)}px`;
    this.skyEl.style.background = skyExtension(Math.max(0, artTop) / artH);

    // Gap from the mark's lowest drip to the top of the menu, in px. Reported so "the
    // menu clears the spatter" is a number rather than a hope.
    this.clearSpacePx = Math.round(chosen.blockTop - (chosen.artTop + artH * KEYART_MARK_BOTTOM_F));
    this.geom = {
      vw, vh, artTop, artBottom, artH: Math.round(artH),
      plateTop: artBottom,
      /** > 0 would mean the plate is over the artwork. It is the defect, as a number. */
      plateOverArtPx: Math.max(0, artTop + (artBottom - artTop) - artBottom),
      blockTop: Math.round(chosen.blockTop),
      markBottom: Math.round(chosen.artTop + artH * KEYART_MARK_BOTTOM_F),
      markTop: Math.round(chosen.artTop + artH * KEYART_MARK_TOP_F),
      clearSpacePx: this.clearSpacePx,
      textAtArtF: +chosen.textF.toFixed(3),
      topCropF: +chosen.topCropF.toFixed(3),
      row: chosen.row,
    };
  }

  private refreshStats(): void {
    const put = (node: HTMLDivElement | null, value: string, unit: string): void => {
      if (!node) return;
      node.textContent = value;
      node.appendChild(el('i', undefined, unit));
    };
    put(this.statDist, this.distanceM.toFixed(0), 'm');
    put(this.statSpeed, this.topSpeedKmh.toFixed(0), 'km/h');
    const s = Math.floor(this.rideSec);
    put(this.statTime, `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`, '');
  }

  // -- per-frame ------------------------------------------------------------

  private frames = 0;

  tick(ctx: SystemContext, dt: number): void {
    const b = ctx.bike;
    if (this.current === null) this.rideSec += Math.max(0, Math.min(0.25, dt));
    if (this.haveLast) {
      const dx = b.x - this.lastX;
      const dz = b.z - this.lastZ;
      const d = Math.sqrt(dx * dx + dz * dz);
      // A reset teleports; do not bank the jump as distance ridden.
      if (d < 4) this.distanceM += d;
    }
    this.lastX = b.x;
    this.lastZ = b.z;
    this.haveLast = true;
    const kmh = b.speed * 3.6;
    if (kmh > this.topSpeedKmh) this.topSpeedKmh = kmh;

    this.frames++;
    if (this.current === null) return;

    // Automation guard #2. If a screen is up and this module has already stopped the
    // loop, nothing should be calling update() at all — the loop is the only thing that
    // does. Another update means something external is stepping frames, i.e. a capture
    // harness whose `navigator.webdriver` did not give it away. Stand down for the
    // session so no other piece's screenshots have a title screen in front of them.
    if (this.stoppedLoop) {
      if (!this.pinned) {
        this.standDown = true;
        this.stoppedLoop = false;
        this.close();
      }
      return;
    }
    // The title opens during init(), which is before main.ts assigns `window.__grip` and
    // before it calls loop.start(). So the first stop attempt has nothing to stop; this
    // is the retry, and it lands on the first frame the loop actually runs.
    this.pauseLoop();
  }

  // -- entry ----------------------------------------------------------------

  boot(): void {
    const params = new URLSearchParams(window.location.search);
    const forced = params.get('ui');
    const ids: ScreenId[] = ['title', 'pause', 'setup', 'controls'];
    if (forced && (ids as string[]).includes(forced)) {
      this.pinned = true;
      this.open(forced as ScreenId);
      return;
    }
    if (forced === 'none') { this.standDown = true; return; }
    // Automation guard #1: under a driver, never show and never fetch the key art.
    if (navigator.webdriver === true) { this.standDown = true; return; }
    this.open('title');
  }

  api(): Record<string, unknown> {
    return {
      show: (id: string) => {
        const ids: ScreenId[] = ['title', 'pause', 'setup', 'controls'];
        if (!(ids as string[]).includes(id)) return false;
        this.pinned = true;
        this.standDown = false;
        this.open(id as ScreenId);
        return true;
      },
      hide: () => { this.pinned = false; this.close(); this.resumeLoop(); return true; },
      /** Which screen is up, or null while riding. */
      current: () => this.current,
      /** For a critic checking that the mark and the art actually resolved. */
      assets: () => ({ wordmark: WORDMARK_URL, keyart: KEYART_URL, loaded: this.artLoaded }),
      frames: () => this.frames,
      /** Distance from the mark's lowest pixel to the top of the title menu, px. */
      clearSpacePx: () => this.clearSpacePx,
      /**
       * The resolved title layout. `plateOverArtPx` is the round-1 defect expressed as a
       * number: any value above 0 means a flat plate is sitting on top of live artwork.
       */
      geometry: () => this.geom,
      /** The resolved overlay frame. The same for pause, setup and controls. */
      overlay: () => this.oGeom,
      /**
       * The round-2 defect as a measurement, taken off the live DOM rather than off the
       * stylesheet, for whichever overlay is currently up:
       *
       *   panelW / panelPct   were 781 / 1041 / 1165 px and 40.8% of an ultrawide
       *   voidTop / voidBottom  space above and below the body's content. Round 2 left
       *                         ~115 px over and ~180 px under on setup; symmetric is the
       *                         target, and |voidTop - voidBottom| is the error
       *   colShortfallPx      how far the shortest column of the body's last row stops
       *                         above the tallest. Controls read 400 px in round 2
       *   markClearPx         smallest gap from the mark's box to anything else, against
       *                         the cap height the mark is owed
       *   undesignedPct       share of the frame that is neither panel, plate nor cap —
       *                         i.e. the live game window. It is *framed* now, but the
       *                         number is what a critic asked about, so it is reported.
       */
      compose: () => {
        const id = this.current;
        if (!id || id === 'title') return null;
        const s = this.screens.get(id);
        if (!s) return null;
        const q = <T extends Element>(sel: string) => s.root.querySelector<T>(sel);
        const box = (e: Element | null) => (e ? e.getBoundingClientRect() : null);
        const panel = box(q('.gm-panel'));
        const body = box(q('.gm-body'));
        const plate = box(q('.gm-footplate'));
        const markEl = q('.gm-head-mark');
        const markB = box(markEl);
        const nameB = box(q('.gm-screenname'));
        const headB = box(q('.gm-head'));
        if (!panel || !body || !plate || !markB || !headB) return null;

        const kids = Array.from(q<HTMLElement>('.gm-body')!.children) as HTMLElement[];
        const tops = kids.map((k) => k.getBoundingClientRect().top);
        const bots = kids.map((k) => k.getBoundingClientRect().bottom);
        const contentTop = Math.min(...tops);
        const contentBottom = Math.max(...bots);

        // Worst column shortfall across every two-column row in the body.
        let colShortfall = 0;
        for (const row of Array.from(s.root.querySelectorAll<HTMLElement>('.gm-cols'))) {
          const cs = Array.from(row.children).map((c) => c.getBoundingClientRect().bottom);
          if (cs.length > 1) colShortfall = Math.max(colShortfall, Math.max(...cs) - Math.min(...cs));
        }

        const owed = Math.round(markB.width * 0.242);
        const clear = Math.min(
          markB.top - headB.top,
          headB.bottom - markB.bottom,
          markB.left - panel.left,
          nameB ? nameB.left - markB.right : Infinity,
        );
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        return {
          screen: id, vw, vh,
          panelW: Math.round(panel.width),
          panelPct: +((panel.width / vw) * 100).toFixed(1),
          plateW: Math.round(plate.width),
          plateH: Math.round(plate.height),
          plateFullWidth: Math.abs(plate.width - vw) < 1.5,
          /** The title's `plateOverArtPx` invariant, applied to the overlay frame: above
           *  zero means the foot plate is sitting on top of the panel rather than under
           *  it. It read 29 px until `.gm-footplate` was made border-box. */
          plateOverPanelPx: Math.max(0, Math.round(panel.bottom - plate.top)),
          voidTop: Math.round(contentTop - body.top),
          voidBottom: Math.round(body.bottom - contentBottom),
          voidErrorPx: Math.round(Math.abs((contentTop - body.top) - (body.bottom - contentBottom))),
          colShortfallPx: Math.round(colShortfall),
          markW: Math.round(markB.width),
          markClearPx: Math.round(clear),
          markClearOwedPx: owed,
          undesignedPct: +((((vw - panel.width) * (vh - plate.height - 7)) / (vw * vh)) * 100).toFixed(1),
          overflowPx: Math.round(Math.max(0, contentBottom - body.bottom)),
        };
      },
    };
  }
}

// ---------------------------------------------------------------------------

const menus = new Menus();

registerSystem({
  id: 'menus',
  order: 120,
  init(ctx: SystemContext) {
    if (typeof document === 'undefined') return;
    try {
      menus.build(ctx.dom);
      (window as unknown as HookWindow).__gripMenus = menus.api();
      menus.boot();
    } catch (e) {
      console.error('[menus] init failed', e);
    }
  },
  update(dt: number, ctx: SystemContext) {
    menus.tick(ctx, dt);
  },
});

export {};
